<template>
    <div class="container mb-8 mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">{{ __('Tervezés') }}</h1>

        <c-control-select class="mx-auto w-96" class-field="bg-white" name="group"
                          v-bind:label="__('Felhasználói csoport')"
                          v-bind:options="groups"
                          v-model="group"
        />
    </div>

    <div class="mx-auto px-2 w-full">
        <div class="bg-slate-800 flex flex-col mb-8 md:flex-row md:items-center px-4 py-1 text-white">
            <span class="font-bold mb-4 md:mb-0 md:mr-8 text-lg">{{ __('Műveletek') }}:</span>

            <div class="flex items-center mb-2 md:mb-0">
                <span class="mr-2 text-sm">{{ __('Másolás innen') }}:</span>
                <c-control-select class="!mb-0 mr-2 w-48" name="group"
                                  v-bind:options="groupsExceptCurrent"
                                  v-model="groupBase"
                />
                <a class="bg-white cursor-pointer hover:bg-highlight hover:text-white lg:mr-2 px-2 py-1 text-slate-800 text-sm"
                   v-on:click="copyFromBase"
                >
                    {{ __('Másol') }}
                </a>
            </div>
        </div>
    </div>

    <div class="bg-white px-2 w-full" v-if="group && products">
        <div class="bg-highlight flex items-center justify-between px-2 py-4 sticky text-white top-[var(--banner-offset)] z-30">
            <a class="cursor-pointer flex items-center text-lg text-white uppercase"
               v-on:click="prevWeek"
            >
                <c-icon class="h-6 mr-2 stroke-white w-6" icon="chevron-left" />
                <span class="hidden md:inline">{{ __('Előző') }}</span>
            </a>
            <span class="font-serif hidden text-3xl xl:inline">
                {{ __(':week. hét', { week: currentWeek }) }} <small class="font-sans text-base">({{ currentYear }})</small>
            </span>
            <span class="font-serif text-3xl xl:hidden">
                {{ DateTime.fromISO(currentDate).toFormat(localizationStore.dateFormat) }}
            </span>
            <a class="cursor-pointer flex items-center text-lg text-white uppercase"
               v-on:click="nextWeek"
            >
                <span class="hidden md:inline">{{ __('Következő') }}</span>
                <c-icon class="h-6 ml-2 stroke-white w-6" icon="chevron-right" />
            </a>
        </div>
        <div class="bg-white border-b border-b-slate-100 grid grid-cols-5 font-semibold px-2 sticky text-center top-[calc(4rem+var(--banner-offset))] xl:grid-cols-10 z-30">
            <span class="flex items-center justify-center p-4"></span>
            <span class="border-l col-span-2 flex items-center justify-center p-4 xl:col-span-3">
                {{ __('Termék') }}
            </span>
            <span class="border-l hidden items-center justify-center p-4 xl:flex">
                {{ __('Ár') }}
            </span>
            <span class="border-l col-span-2 hidden p-4 xl:col-span-1 xl:block"
                  v-bind:class="{ '!block' : currentDate.weekdayLong.toLowerCase() === 'monday' }"
            >
                {{ __('Hétfő') }}<br />
                <span class="hidden xl:block">{{ currentDays.monday.month }} {{ currentDays.monday.day }}</span>
                <a class="cursor-pointer font-normal text-xs" v-on:click="resetAvailabilityData('monday')">
                    ❌ {{ __('Nullázás') }}
                </a>
            </span>
            <span class="border-l col-span-2 hidden p-4 xl:col-span-1 xl:block"
                  v-bind:class="{ '!block' : currentDate.weekdayLong.toLowerCase() === 'tuesday' }"
            >
                {{ __('Kedd') }}<br />
                <span class="hidden xl:block">{{ currentDays.tuesday.month }} {{ currentDays.tuesday.day }}</span>
                <a class="cursor-pointer font-normal text-xs" v-on:click="resetAvailabilityData('tuesday')">
                    ❌ {{ __('Nullázás') }}
                </a>
            </span>
            <span class="border-l col-span-2 hidden p-4 xl:col-span-1 xl:block"
                  v-bind:class="{ '!block' : currentDate.weekdayLong.toLowerCase() === 'wednesday' }"
            >
                {{ __('Szerda') }}<br />
                <span class="hidden xl:block">{{ currentDays.wednesday.month }} {{ currentDays.wednesday.day }}</span>
                <a class="cursor-pointer font-normal text-xs" v-on:click="resetAvailabilityData('wednesday')">
                    ❌ {{ __('Nullázás') }}
                </a>
            </span>
            <span class="border-l col-span-2 hidden p-4 xl:col-span-1 xl:block"
                  v-bind:class="{ '!block' : currentDate.weekdayLong.toLowerCase() === 'thursday' }"
            >
                {{ __('Csütörtök') }}<br />
                <span class="hidden xl:block">{{ currentDays.thursday.month }} {{ currentDays.thursday.day }}</span>
                <a class="cursor-pointer font-normal text-xs" v-on:click="resetAvailabilityData('thursday')">
                    ❌ {{ __('Nullázás') }}
                </a>
            </span>
            <span class="border-l col-span-2 hidden p-4 xl:col-span-1 xl:block"
                  v-bind:class="{ '!block' : currentDate.weekdayLong.toLowerCase() === 'friday' }"
            >
                {{ __('Péntek') }}<br />
                <span class="hidden xl:block">{{ currentDays.friday.month }} {{ currentDays.friday.day }}</span>
                <a class="cursor-pointer font-normal text-xs" v-on:click="resetAvailabilityData('friday')">
                    ❌ {{ __('Nullázás') }}
                </a>
            </span>
        </div>
        <div v-for="product in products">
            <div class="border-t grid grid-cols-5 px-2 xl:grid-cols-10"
                 v-if="product.availability[`${group}`] && product.availability[`${group}`][`${currentYear}.${currentWeek}`] && product.availability[`${group}`][`${currentYear}.${currentWeek}`].monday && product.availability[`${group}`][`${currentYear}.${currentWeek}`].tuesday && product.availability[`${group}`][`${currentYear}.${currentWeek}`].wednesday && product.availability[`${group}`][`${currentYear}.${currentWeek}`].thursday && product.availability[`${group}`][`${currentYear}.${currentWeek}`].friday"
            >
                <span class="flex items-center justify-center p-1">
                    <img class="max-w-32 w-full" v-bind:alt="product.name" v-bind:src="product.picture" />
                </span>
                <span class="border-l col-span-2 flex flex-col items-start justify-center p-4 xl:col-span-3">
                    <strong class="block text-slate-800 text-sm">{{ product.name }}</strong>
                    <span class="mt-1 text-slate-500 text-xs">{{ product.description }}</span>

                    <div class="flex flex-col xl:hidden">
                        <span class="mt-4">
                            {{ currencyStore.format(product.prices[`${group}`], 'huf') }}
                        </span>
                        <span class="text-xs">
                            ({{ currencyStore.format(product.prices[`${group}`] / (1 + product.vat_pct / 100), 'huf') }} + {{ __('ÁFA') }})
                        </span>
                    </div>
                </span>
                <span class="border-l hidden items-center justify-center p-4 text-center xl:flex">
                    <div class="flex flex-col">
                        <span>
                            {{ currencyStore.format(product.prices[`${group}`], 'huf') }}
                        </span>
                        <span class="text-xs">
                            ({{ currencyStore.format(product.prices[`${group}`] / (1 + product.vat_pct / 100), 'huf') }} + {{ __('ÁFA') }})
                        </span>
                    </div>
                </span>
                <span class="border-l col-span-2 flex-col hidden items-center justify-center p-4 xl:col-span-1 xl:flex"
                      v-bind:class="{ '!flex' : currentDate.weekdayLong.toLowerCase() === 'monday' }"
                >
                    <c-control-number class="w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Összesen')"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.monday.total'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].monday.total"
                                      v-on:update:model-value="updateTotalAvailability(product, group, currentYear, currentWeek, 'monday')"
                    />
                    <c-control-number class="mt-4 w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Vásárlónként')"
                                      v-bind:max="product.availability[`${group}`][`${currentYear}.${currentWeek}`].monday.total"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.monday.perPerson'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].monday.perPerson"
                                      v-on:update:model-value="checkMaxPerPerson(product, group, currentYear, currentWeek,'monday')"
                    />
                </span>
                <span class="border-l col-span-2 flex-col hidden items-center justify-center p-4 xl:col-span-1 xl:flex"
                      v-bind:class="{ '!flex' : currentDate.weekdayLong.toLowerCase() === 'tuesday' }"
                >
                    <c-control-number class="w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Összesen')"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.tuesday.total'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].tuesday.total"
                                      v-on:update:model-value="updateTotalAvailability(product, group, currentYear, currentWeek, 'tuesday')"
                    />
                    <c-control-number class="mt-4 w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Vásárlónként')"
                                      v-bind:max="product.availability[`${group}`][`${currentYear}.${currentWeek}`].tuesday.total"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.tuesday.perPerson'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].tuesday.perPerson"
                                      v-on:update:model-value="checkMaxPerPerson(product, group, currentYear, currentWeek,'tuesday')"
                    />
                </span>
                <span class="border-l col-span-2 flex-col hidden items-center justify-center p-4 xl:col-span-1 xl:flex"
                      v-bind:class="{ '!flex' : currentDate.weekdayLong.toLowerCase() === 'wednesday' }"
                >
                    <c-control-number class="w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Összesen')"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.wednesday.total'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].wednesday.total"
                                      v-on:update:model-value="updateTotalAvailability(product, group, currentYear, currentWeek, 'wednesday')"
                    />
                    <c-control-number class="mt-4 w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Vásárlónként')"
                                      v-bind:max="product.availability[`${group}`][`${currentYear}.${currentWeek}`].wednesday.total"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.wednesday.perPerson'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].wednesday.perPerson"
                                      v-on:update:model-value="checkMaxPerPerson(product, group, currentYear, currentWeek,'wednesday')"
                    />
                </span>
                <span class="border-l col-span-2 flex-col hidden items-center justify-center p-4 xl:col-span-1 xl:flex"
                      v-bind:class="{ '!flex' : currentDate.weekdayLong.toLowerCase() === 'thursday' }"
                >
                    <c-control-number class="w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Összesen')"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.thursday.total'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].thursday.total"
                                      v-on:update:model-value="updateTotalAvailability(product, group, currentYear, currentWeek, 'thursday')"
                    />
                    <c-control-number class="mt-4 w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Vásárlónként')"
                                      v-bind:max="product.availability[`${group}`][`${currentYear}.${currentWeek}`].thursday.total"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.thursday.perPerson'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].thursday.perPerson"
                                      v-on:update:model-value="checkMaxPerPerson(product, group, currentYear, currentWeek,'thursday')"
                    />
                </span>
                <span class="border-l col-span-2 flex-col hidden items-center justify-center p-4 xl:col-span-1 xl:flex"
                      v-bind:class="{ '!flex' : currentDate.weekdayLong.toLowerCase() === 'friday' }"
                >
                    <c-control-number class="w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Összesen')"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.friday.total'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].friday.total"
                                      v-on:update:model-value="updateTotalAvailability(product, group, currentYear, currentWeek, 'friday')"
                    />
                    <c-control-number class="mt-4 w-20" class-field="!bg-slate-800 !text-white" pattern="\d*"
                                      v-bind:label="__('Vásárlónként')"
                                      v-bind:max="product.availability[`${group}`][`${currentYear}.${currentWeek}`].friday.total"
                                      v-bind:min="1"
                                      v-bind:name="product.id + '.' + group + '.' + currentYear + '.' + currentWeek + '.friday.perPerson'"
                                      v-bind:step="1"
                                      v-model.number="product.availability[`${group}`][`${currentYear}.${currentWeek}`].friday.perPerson"
                                      v-on:update:model-value="checkMaxPerPerson(product, group, currentYear, currentWeek,'friday')"
                    />
                </span>
            </div>
        </div>
    </div>

    <c-button class="bg-slate-800 border-2 border-slate-800 font-semibold hover:bg-highlight hover:border-highlight mb-4 mt-8 mx-auto normal-case px-4 py-2 rounded-none table text-center !text-lg text-white transition-colors w-48"
              v-on:click="submit"
    >
        {{ __('Mentés') }}
    </c-button>

    <div class="bottom-2 fixed flex items-center right-2 z-20">
        <a class="bg-success block border border-white cursor-pointer mr-2 p-2 rounded-full shadow-lg"
           v-on:click="submit"
        >
            <c-icon class="h-8 stroke-white w-8" icon="save" />
        </a>
        <a class="bg-slate-800 block border border-white cursor-pointer p-2 rounded-full shadow-lg"
           v-on:click="scrollToTop"
        >
            <c-icon class="h-8 stroke-white w-8" icon="chevron-up" />
        </a>
    </div>
</template>

<script setup lang="ts">
    import { computed, inject, onMounted, ref } from 'vue';
    import {
        CButton,
        CControlNumber,
        CControlSelect,
        CIcon,
    } from '@teamfurther/cinderblock';
    import { DateTime } from 'luxon';
    import ProductRepository from '../../repositories/ProductRepository';
    import UserGroupRepository from '../../repositories/UserGroupRepository';
    import { useCurrencyStore } from '../../stores/currency';
    import { useLocalizationStore } from '../../stores/localization';
    import { useRequestStore } from '../../stores/request';

    const __ = inject('__');
    const breakpoint = 1280;
    const currencyStore = useCurrencyStore();
    const currentBreakpoint = ref(window.innerWidth);
    const currentDate = ref(DateTime.now().setLocale('en'));
    const localizationStore = useLocalizationStore();
    const currentDays = computed(() => {
        const firstDay = currentDate.value.startOf('week');

        return {
            monday: {
                date: firstDay,
                day: firstDay.day,
                month: firstDay.setLocale(localizationStore.locale).toFormat('MMM')
            },
            tuesday: {
                date: firstDay.plus({ days: 1 }),
                day: firstDay.plus({ days: 1 }).day,
                month: firstDay.plus({ days: 1 }).setLocale(localizationStore.locale).toFormat('MMM')
            },
            wednesday: {
                date: firstDay.plus({ days: 2 }),
                day: firstDay.plus({ days: 2 }).day,
                month: firstDay.plus({ days: 2 }).setLocale(localizationStore.locale).toFormat('MMM')
            },
            thursday: {
                date: firstDay.plus({ days: 3 }),
                day: firstDay.plus({ days: 3 }).day,
                month: firstDay.plus({ days: 3 }).setLocale(localizationStore.locale).toFormat('MMM')
            },
            friday: {
                date: firstDay.plus({ days: 4 }),
                day: firstDay.plus({ days: 4 }).day,
                month: firstDay.plus({ days: 4 }).setLocale(localizationStore.locale).toFormat('MMM')
            },
        }
    });
    const currentWeek = computed(() => {
        return currentDate.value.weekNumber;
    });
    const currentYear = computed(() => {
        return currentDate.value.year;
    });
    const group = ref();
    const groupBase = ref(null);
    const groups = ref([]);
    const groupsExceptCurrent = computed(() => groups.value.filter((g) => g.value !== group.value));
    const productRepository = new ProductRepository();
    const products = ref();
    const requestStore = useRequestStore();
    const userGroupRepository = new UserGroupRepository();

    function addMissingAvailabilityData() {
        products.value.forEach((product) => {
            if (!product.quantity) {
                product.quantity = {};
            }

            groups.value.forEach((group) => {
                if (!product.availability[`${group.value}`]) {
                    product.availability[`${group.value}`] = {};
                }

                product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`] = {
                    monday: product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`]?.monday ?? { perPerson: null, total: null },
                    tuesday: product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`]?.tuesday ?? { perPerson: null, total: null },
                    wednesday: product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`]?.wednesday ?? { perPerson: null, total: null },
                    thursday: product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`]?.thursday ?? { perPerson: null, total: null },
                    friday: product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`]?.friday ?? { perPerson: null, total: null },
                };
            });
        });
    }

    function checkMaxPerPerson(product, group, currentYear, currentWeek, day) {
        const total = product.availability[`${group}`][`${currentYear}.${currentWeek}`][day].total;
        const perPerson = product.availability[`${group}`][`${currentYear}.${currentWeek}`][day].perPerson;

        if (perPerson > total) {
            product.availability[`${group}`][`${currentYear}.${currentWeek}`][day].perPerson = total;
        }

        updateTotalAvailability(product, group, currentYear, currentWeek, day);
    }

    function copyFromBase() {
        if (!groupBase.value) {
            return;
        }

        products.value.forEach((product) => {
            if (product.availability[`${groupBase.value}`]) {
                product.availability[`${group.value}`] = { ...product.availability[`${groupBase.value}`] };
            } else {
                product.availability[`${group.value}`] = {
                    monday: { perPerson: null, total: null },
                    tuesday: { perPerson: null, total: null },
                    wednesday: { perPerson: null, total: null },
                    thursday: { perPerson: null, total: null },
                };
            }
        });

        addMissingAvailabilityData();

        groupBase.value = null;
    }

    async function getProducts() {
        products.value = await productRepository.index({
            filters: {
                active: true,
            },
        });

        addMissingAvailabilityData();
    }

    function nextWeek() {
        currentDate.value = breakpoint < currentBreakpoint.value
            ? currentDate.value.plus({ weeks: 1 })
            : currentDate.value.plus({ days: 1 });

        if (currentDate.value.weekday === 6) {
            currentDate.value = currentDate.value.plus({ day: 2 });
        }

        addMissingAvailabilityData();
    }

    function prevWeek() {
        currentDate.value = breakpoint < currentBreakpoint.value
            ? currentDate.value.minus({ weeks: 1 })
            : currentDate.value.minus({ days: 1 });

        if (currentDate.value.weekday === 7) {
            currentDate.value = currentDate.value.minus({ day: 2 });
        }

        addMissingAvailabilityData();
    }

    function resetAvailabilityData(day) {
        products.value.forEach((product) => {
            groups.value.forEach((group) => {
                product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`][day].perPerson = 0;
                product.availability[`${group.value}`][`${currentYear.value}.${currentWeek.value}`][day].total = 0;
            });
        });
    }

    function scrollToTop() {
        window.scrollTo({
            behavior: 'smooth',
            top: 0,
        });
    }

    async function submit() {
        const weekKey = `${currentYear.value}.${currentWeek.value}`;
        const payload: Record<string, any> = {};

        for (const product of products.value) {
            const productData: Record<string, any> = {};

            for (const g of groups.value) {
                const groupKey = (g as any).value;

                if (product.availability[groupKey]?.[weekKey]) {
                    if (!productData[groupKey]) {
                        productData[groupKey] = {};
                    }

                    productData[groupKey][weekKey] = product.availability[groupKey][weekKey];
                }
            }

            if (Object.keys(productData).length > 0) {
                payload[product.id] = productData;
            }
        }

        if (Object.keys(payload).length > 0) {
            await requestStore.put('products/availability', { products: payload });
        }

        requestStore.message = __('Termékek elérhetősége sikeresen frissítve.');
        requestStore.status = 200;
        requestStore.title = __('Siker');
    }

    function updateTotalAvailability(product, group, currentYear, currentWeek, day) {
        groups.value.forEach((g) => {
            if (g.value === group) {
                return;
            }

            if (product.availability[g.value]?.[`${currentYear}.${currentWeek}`]?.[day]) {
                product.availability[g.value][`${currentYear}.${currentWeek}`][day].total = product.availability[group][`${currentYear}.${currentWeek}`][day].total;
            }
        });
    }

    onMounted(async () => {
        const userGroups = await userGroupRepository.index({
            filters: {
                active: true,
            },
        });
        userGroups.forEach((g) => {
            groups.value.push({
                label: g.name,
                value: g.id,
            });

            if (g.name === 'Általános') {
                group.value = g.id;
            }
        });

        await getProducts();

        window.addEventListener('resize', () => {
            currentBreakpoint.value = window.innerWidth;
        });

        if (currentDate.value.weekday === 6) {
            currentDate.value = currentDate.value.plus({ day: 2 });
        } else if (currentDate.value.weekday === 7) {
            currentDate.value = currentDate.value.plus({ day: 1 });
        }
    });
</script>
